/**
 * Core value types for the rules engine.
 *
 * The engine is deliberately headless and configuration-driven (§10, "Rule
 * variance"): Indian Rummy is one member of a family, so deck count, joker
 * rules, sequence requirements and the scoring table are all parameters rather
 * than constants. v1 ships a single configuration.
 */

export type Suit = 'S' | 'H' | 'D' | 'C';

export type Rank =
  | 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

export const SUITS: readonly Suit[] = ['S', 'H', 'D', 'C'];
export const RANKS: readonly Rank[] = [
  'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K',
];

/**
 * Stable identity for a physical card. Two decks are in play, so `copy`
 * distinguishes the otherwise-identical 7♠ from the other 7♠ — this matters:
 * two identical cards in one set is invalid (§3.2).
 *
 * Format: `7S:0`, `AH:1`, `JK:0`.
 */
export type CardId = string;

export interface Card {
  readonly id: CardId;
  /** `null` for a printed joker. */
  readonly suit: Suit | null;
  /** `null` for a printed joker. */
  readonly rank: Rank | null;
  readonly copy: number;
  readonly printedJoker: boolean;
}

export type PlayerId = string;

/** A user-defined lane in the tray (§6.3). Card order within a group is the
 *  player's own arrangement and is preserved verbatim. */
export type Group = CardId[];

export type MeldKind = 'pure-sequence' | 'impure-sequence' | 'set';

export interface MeldClassification {
  kind: MeldKind;
  /** Cards used as wilds rather than at their face identity. */
  wildsUsed: number;
}

export interface ScoringTable {
  /** Points for A/J/Q/K. */
  readonly highCard: number;
  /** Points a joker contributes when ungrouped. */
  readonly joker: number;
  /** Maximum a losing hand can be scored, before drops. */
  readonly cap: number;
  /**
   * Minimum a losing hand can be scored (§3.6). Zero belongs to the winner: a
   * player holding a complete show when somebody else declares first has lost
   * the hand, and scoring them the same as the player who actually closed it
   * erases the only thing that separated them — who got there first.
   */
  readonly losingFloor: number;
  readonly invalidDeclaration: number;
  readonly firstDrop: number;
  readonly middleDrop: number;
}

export interface RuleConfig {
  readonly id: string;
  readonly deckCount: number;
  readonly printedJokers: number;
  readonly handSize: number;
  /** Minimum combinations required to declare (§3.3). */
  readonly minSequences: number;
  readonly minPureSequences: number;
  readonly minMeldSize: number;
  readonly maxSetSize: number;
  readonly aceLow: boolean;
  readonly aceHigh: boolean;
  /** K-A-2. Invalid in Indian Rummy (§3.2). */
  readonly roundTheCorner: boolean;
  /** Whether a card of the wild rank may also be played at face identity —
   *  e.g. with 5♦ wild, 4♦5♦6♦ is a *pure* sequence. */
  readonly wildCardsMayPlayNatural: boolean;
  /** Whether a meld composed entirely of wilds counts. House rules differ;
   *  most Indian platforms accept it. */
  readonly allowAllWildMeld: boolean;
  /** Whether the card just taken from the open pile may be thrown straight
   *  back in the same turn. Standard rummy says no, and without the rule a
   *  turn can be a no-op. */
  readonly mayDiscardDrawnOpenCard: boolean;
  /** Rank that becomes wild when the cut card is itself a printed joker (§3.1). */
  readonly wildOnPrintedJokerCut: Rank;
  readonly scoring: ScoringTable;
}

/** Per-hand context: the config plus what turned out to be wild. */
export interface MeldContext {
  readonly config: RuleConfig;
  /** All four suits of this rank, across both decks, are wild. `null` when the
   *  variant has no wild joker. */
  readonly wildRank: Rank | null;
}

export type SessionFormat = 'deals-2' | 'deals-3' | 'deals-6' | 'pool-101' | 'pool-201';

export type PlayerOutcome =
  | 'won'
  | 'lost'
  | 'first-drop'
  | 'middle-drop'
  | 'invalid-declaration'
  | 'auto-drop';
